import html2canvas from "html2canvas";

export const QUOTE_POSTER_WIDTH = 414;
const CAPTURE_SCALE = 2;

const MAIN_FONT_SIZES = [8, 9, 10, 11, 12, 13, 15, 16, 18, 22];

function sanitizeFilename(name) {
    return (
        String(name || "quotation")
            .trim()
            .replace(/[^\w\u4e00-\u9fff\- ]+/g, "")
            .replace(/\s+/g, "-")
            .slice(0, 60) || "quotation"
    );
}

function getMainFontsUrl() {
    const base = process.env.PUBLIC_URL || "";
    const prefix = base.endsWith("/") ? base.slice(0, -1) : base;
    return `${window.location.origin}${prefix}/fonts/1.ttf`;
}

export async function ensureQuotePosterFontsLoaded() {
    if (!document.fonts?.load) {
        return;
    }

    try {
        await Promise.race([
            Promise.all(MAIN_FONT_SIZES.map((size) => document.fonts.load(`${size}px MainFonts`))),
            document.fonts.ready,
            new Promise((resolve) => {
                window.setTimeout(resolve, 2500);
            }),
        ]);
    } catch {
        // Fallback stack will still render if custom font is unavailable.
    }
}

function prepareCloneForCapture(clonedDoc, clonedElement) {
    const style = clonedDoc.createElement("style");
    style.textContent = `
        @font-face {
            font-family: MainFonts;
            src: url("${getMainFontsUrl()}") format("truetype");
        }
        .quote-poster {
            width: ${QUOTE_POSTER_WIDTH}px !important;
            max-width: none !important;
            aspect-ratio: unset !important;
            margin: 0 !important;
            padding: 40px 0 32px !important;
            box-sizing: border-box !important;
        }
        .quote-poster__inner {
            padding: 0 34px !important;
            box-sizing: border-box !important;
        }
        .quote-poster__brand,
        .quote-poster__doc-title,
        .quote-poster__project,
        .quote-poster__meta,
        .quote-poster__section-title,
        .quote-poster__item-amount,
        .quote-poster__total-label,
        .quote-poster__total-amount,
        .quote-poster__section-subtotal span:last-child,
        .quote-poster__website {
            font-family: MainFonts, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
        }
        .quote-poster__brand {
            padding-left: 0.45em;
        }
        .quote-poster__website {
            padding-left: 0.35em;
        }
    `;
    clonedDoc.head.appendChild(style);

    if (clonedElement) {
        clonedElement.style.width = `${QUOTE_POSTER_WIDTH}px`;
        clonedElement.style.maxWidth = "none";
        clonedElement.style.aspectRatio = "unset";
        clonedElement.style.margin = "0";
    }
}

function applyCaptureDimensions(element) {
    element.style.width = `${QUOTE_POSTER_WIDTH}px`;
    element.style.maxWidth = `${QUOTE_POSTER_WIDTH}px`;
    element.style.aspectRatio = "unset";
    element.style.margin = "0";
    element.style.boxSizing = "border-box";
}

function restoreCaptureDimensions(element, previous) {
    element.style.width = previous.width;
    element.style.maxWidth = previous.maxWidth;
    element.style.aspectRatio = previous.aspectRatio;
    element.style.margin = previous.margin;
    element.style.boxSizing = previous.boxSizing;
}

export async function captureQuotePoster(element) {
    if (!element) {
        throw new Error("Quote poster element is missing");
    }

    await ensureQuotePosterFontsLoaded();

    const previous = {
        width: element.style.width,
        maxWidth: element.style.maxWidth,
        aspectRatio: element.style.aspectRatio,
        margin: element.style.margin,
        boxSizing: element.style.boxSizing,
    };

    applyCaptureDimensions(element);

    try {
        await new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        const canvas = await html2canvas(element, {
            scale: CAPTURE_SCALE,
            backgroundColor: "#fbfbfd",
            useCORS: true,
            logging: false,
            scrollX: 0,
            scrollY: 0,
            windowWidth: QUOTE_POSTER_WIDTH,
            onclone: prepareCloneForCapture,
        });

        return canvas;
    } finally {
        restoreCaptureDimensions(element, previous);
    }
}

export function makeQuotePosterFilename(projectName, quoteDateLabel) {
    const datePart = String(quoteDateLabel || "").replace(/\./g, "-") || "date";
    return `${sanitizeFilename(projectName)}-quotation-${datePart}.png`;
}

export function quotePosterCanvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("Quote poster canvas conversion failed"));
                }
            },
            "image/png"
        );
    });
}

export async function getQuotePosterAsset(element, projectName, quoteDateLabel) {
    const canvas = await captureQuotePoster(element);
    const filename = makeQuotePosterFilename(projectName, quoteDateLabel);
    return {
        canvas,
        dataUrl: canvas.toDataURL("image/png"),
        blob: await quotePosterCanvasToBlob(canvas),
        filename,
    };
}

export async function getQuotePosterImage(element, projectName, quoteDateLabel) {
    const { dataUrl, filename } = await getQuotePosterAsset(
        element,
        projectName,
        quoteDateLabel
    );
    return { dataUrl, filename };
}

export async function downloadQuotePosterFromElement(element, projectName, quoteDateLabel) {
    const { dataUrl, filename } = await getQuotePosterAsset(element, projectName, quoteDateLabel);
    const link = document.createElement("a");
    link.download = filename;
    link.href = dataUrl;
    link.click();
}
